import { useState } from 'react';
import { BarChart, LayoutGrid, LineChart } from 'lucide-react';
import MetricStackedBarChart from 'src/pages/reports/components/metric-stacked-bar-chart/metric-stacked-bar-chart';
import MetricChart from 'src/pages/reports/components/metric-chart';
import SurveyGroupTrendsFacetLineChart from './components/survey-group-trends-facet-line-chart';
import type { GroupTrendsQuestionChartModel } from './survey-group-trends-merge.helpers';

interface SurveyGroupTrendsQuestionChartProps {
  block: GroupTrendsQuestionChartModel;
  index: number;
  isLoading: boolean;
}

type LayoutMode = 'facets' | 'combined';

/**
 * Survey group trends: default is one line chart per survey (answer options only).
 * Optional "all series" view keeps the previous single-chart bar/line toggle.
 */
const SurveyGroupTrendsQuestionChart = ({ block, index, isLoading }: SurveyGroupTrendsQuestionChartProps) => {
  const [layoutMode, setLayoutMode] = useState<LayoutMode>('facets');
  const [chartType, setChartType] = useState<'bar' | 'line'>('line');

  if (block.skipReason === 'freeform') {
    return (
      <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm md:p-6">
        <h2 className="text-lg font-semibold text-gray-900">
          {index + 1}
          .
          {' '}
          {block.title}
        </h2>
        <p className="mt-2 text-sm text-gray-600">
          Trend charts are not available for freeform questions in group view.
        </p>
      </section>
    );
  }

  if (!block.hasChart) {
    return (
      <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm md:p-6">
        <h2 className="text-lg font-semibold text-gray-900">
          {index + 1}
          .
          {' '}
          {block.title}
        </h2>
        <p className="mt-2 text-sm text-gray-600">
          No trend intervals returned for this question for the selected surveys or date range. Try a longer interval or confirm each survey has trends enabled.
        </p>
      </section>
    );
  }

  const showFacets = layoutMode === 'facets' && block.facets.length > 0;
  const combinedMetrics = block.metrics.map((m) => ({
    key: m.key,
    name: m.name,
    color: m.color,
    imageUrl: m.answerImageUrl,
  }));

  return (
    <section
      className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden"
      id={`group-trends-q-${block.questionClusterId}`}
    >
      <div className="flex flex-col gap-3 border-b border-gray-100 px-3 md:px-4 pt-3 pb-2">
        <div className="flex flex-row flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-violet-100 text-sm font-semibold text-violet-900">
              {index + 1}
            </span>
            <div>
              <h2 className="text-lg font-semibold text-gray-900 leading-snug">
                {block.title}
              </h2>
              <p className="mt-1 text-xs text-gray-500">
                {showFacets
                  ? 'Each panel is one survey; lines are answer options (percent of respondents).'
                  : 'All surveys and answers on one chart — use the layout toggle above for a clearer per-survey view.'}
              </p>
            </div>
          </div>
          <div
            className="flex flex-wrap items-center gap-1 shrink-0"
            role="group"
            aria-label="Chart layout"
          >
            <button
              type="button"
              className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors ${layoutMode === 'facets' ? 'border-violet-300 bg-violet-50 text-violet-900' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'}`}
              onClick={() => setLayoutMode('facets')}
            >
              <LayoutGrid className="size-3.5" />
              By survey
            </button>
            <button
              type="button"
              className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors ${layoutMode === 'combined' ? 'border-violet-300 bg-violet-50 text-violet-900' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'}`}
              onClick={() => setLayoutMode('combined')}
            >
              <LineChart className="size-3.5" />
              All series
            </button>
          </div>
        </div>
        {layoutMode === 'combined' ? (
          <div className="flex items-stretch justify-end border-t border-gray-100 pt-2 -mx-1">
            <button
              type="button"
              className={`flex flex-col items-center justify-center text-gray-500 hover:opacity-80 px-3 border border-gray-200 rounded-l-md ${chartType === 'bar' ? 'bg-gray-100' : 'bg-white'}`}
              onClick={() => setChartType('bar')}
            >
              <BarChart className={`w-5 h-5 ${chartType === 'bar' ? 'text-gray-900' : 'text-gray-400'}`} />
              <span className="text-[10px] font-medium mt-0.5">
                Bar
              </span>
            </button>
            <button
              type="button"
              className={`flex flex-col items-center justify-center text-gray-500 hover:opacity-80 px-3 border border-l-0 border-gray-200 rounded-r-md ${chartType === 'line' ? 'bg-gray-100' : 'bg-white'}`}
              onClick={() => setChartType('line')}
            >
              <LineChart className={`w-5 h-5 ${chartType === 'line' ? 'text-gray-900' : 'text-gray-400'}`} />
              <span className="text-[10px] font-medium mt-0.5">
                Line
              </span>
            </button>
          </div>
        ) : null}
      </div>
      {showFacets ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 p-4 md:p-5">
          {block.facets.map((facet) => (
            <div
              key={facet.extSurveyId}
              className="rounded-xl border border-gray-100 bg-slate-50/50 p-3 shadow-sm"
            >
              <h3
                className="text-sm font-semibold text-gray-800 mb-1 truncate"
                title={facet.title}
              >
                {facet.title}
              </h3>
              <SurveyGroupTrendsFacetLineChart
                data={facet.chartData}
                metrics={facet.metrics}
                isLoading={isLoading}
                yDomain={[0, 100]}
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="h-[min(440px,75vh)] w-full min-h-[300px] p-2 pb-4">
          {chartType === 'bar' ? (
            <MetricStackedBarChart
              className="border-none !p-2 !pb-4"
              data={block.chartData}
              metrics={combinedMetrics}
              isLoading={isLoading}
              isNormalized={false}
              sharedYAxisDomain
              showBrush={false}
            />
          ) : (
            <MetricChart
              className="border-none !p-2 !pb-4"
              data={block.chartData}
              metrics={combinedMetrics}
              isLoading={isLoading}
              isNormalized={false}
              sharedYAxisDomain
              showBrush={false}
            />
          )}
        </div>
      )}
    </section>
  );
};

export default SurveyGroupTrendsQuestionChart;
